import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckCircleFilled, CustomerServiceOutlined, WarningFilled } from "@ant-design/icons";
import { Button, Spin, theme } from "antd";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";
import { ExpiredPaymentAffectedSites } from "./expiredPaymentAffectedSites.js";
import { __ } from "../../../../utils/i18n.js";
import { getLegalManufacturerUrls } from "../../../../utils/legalEntities.js";
import { useExpiredPaymentDeepLink } from "../../hooks/useExpiredPaymentDeepLink.js";
import { AccountSkeleton } from "../accountSkeleton.js";
import { SlimHero } from "../hero/slimHero.js";
import { ChangePaymentDetailsForm } from "../modals/changePaymentDetailsForm.js";
// Auth-less expired-payment deep-link surface.
// Renders the slim hero but not the tab strip — the URL is the capability (UUID).
const ExpiredPaymentDeepLinkPage = observer(()=>{
    const { uuid: subscriptionUuid } = useParams();
    const { token } = theme.useToken();
    const { support: supportUrl } = getLegalManufacturerUrls();
    const { isBusy, isUpdated, error, subscription, hostnames, markUpdated } = useExpiredPaymentDeepLink(subscriptionUuid);
    const renderContent = ()=>{
        if (isBusy) {
            return /*#__PURE__*/ _jsx(AccountSkeleton, {});
        }
        if (isUpdated) {
            return /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-auth-card-wrap rc-account-v2-auth-card-wrap--wide",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-auth-card",
                    style: {
                        background: token.colorBgContainer,
                        borderColor: token.colorBorder
                    },
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-auth-card__icon rc-account-v2-auth-card__icon--lg",
                            style: {
                                color: token.colorSuccess
                            },
                            children: /*#__PURE__*/ _jsx(CheckCircleFilled, {})
                        }),
                        /*#__PURE__*/ _jsx("h1", {
                            className: "rc-account-v2-auth-card__title",
                            style: {
                                color: token.colorTextHeading
                            },
                            children: __("Payment method updated.")
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "rc-account-v2-auth-card__subtitle",
                            style: {
                                color: token.colorTextSecondary
                            },
                            children: __("We'll take care of the payment automatically with your new method. There's nothing else for you to do.")
                        })
                    ]
                })
            });
        }
        if (error?.hasErrorCode("SubscriptionPaymentDetailsHealthy")) {
            return /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-auth-card-wrap rc-account-v2-auth-card-wrap--wide",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-auth-card",
                    style: {
                        background: token.colorBgContainer,
                        borderColor: token.colorBorder
                    },
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-auth-card__icon rc-account-v2-auth-card__icon--lg",
                            style: {
                                color: token.colorSuccess
                            },
                            children: /*#__PURE__*/ _jsx(CheckCircleFilled, {})
                        }),
                        /*#__PURE__*/ _jsx("h1", {
                            className: "rc-account-v2-auth-card__title",
                            style: {
                                color: token.colorTextHeading
                            },
                            children: __("Looks like the stored payment method is already up to date. No action needed.")
                        })
                    ]
                })
            });
        }
        if (error && error.codes.length > 0) {
            return /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-auth-card-wrap rc-account-v2-auth-card-wrap--wide",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-auth-card",
                    style: {
                        background: token.colorBgContainer,
                        borderColor: token.colorBorder
                    },
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-auth-card__icon rc-account-v2-auth-card__icon--lg",
                            style: {
                                color: token.colorWarning
                            },
                            children: /*#__PURE__*/ _jsx(WarningFilled, {})
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "rc-account-v2-auth-card__subtitle",
                            style: {
                                color: token.colorTextSecondary
                            },
                            children: error.message
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-auth-card__cta",
                            children: /*#__PURE__*/ _jsx(Button, {
                                size: "large",
                                icon: /*#__PURE__*/ _jsx(CustomerServiceOutlined, {}),
                                href: supportUrl,
                                target: "_blank",
                                rel: "noopener noreferrer",
                                children: __("Contact support")
                            })
                        })
                    ]
                })
            });
        }
        if (subscription) {
            const productName = subscription.data.productPlan.product.name;
            return /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-auth-card-wrap rc-account-v2-auth-card-wrap--wide",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-auth-card rc-account-v2-auth-card--left",
                    style: {
                        background: token.colorBgContainer,
                        borderColor: token.colorBorder
                    },
                    children: [
                        /*#__PURE__*/ _jsx("h1", {
                            className: "rc-account-v2-expired-payment-form__heading",
                            style: {
                                color: token.colorTextHeading
                            },
                            children: __("Update your payment method to keep %s active", productName)
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "rc-account-v2-expired-payment-form__description",
                            style: {
                                color: token.colorTextSecondary
                            },
                            children: __("Your subscription renewed for the next term, but we couldn't charge the payment method on file. Please update it before the grace period ends and the subscription is cancelled for non-payment.")
                        }),
                        /*#__PURE__*/ _jsx(ExpiredPaymentAffectedSites, {
                            productName: productName,
                            hostnames: hostnames
                        }),
                        /*#__PURE__*/ _jsx(ChangePaymentDetailsForm, {
                            subscription: subscription,
                            shouldRefetch: false,
                            onClose: markUpdated
                        })
                    ]
                })
            });
        }
        return null;
    };
    return /*#__PURE__*/ _jsxs("main", {
        "aria-label": "Customer Center",
        className: "rc-account-v2-auth",
        children: [
            /*#__PURE__*/ _jsx(SlimHero, {
                isAnonymous: true
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-auth__content",
                children: /*#__PURE__*/ _jsx(Spin, {
                    spinning: isBusy && !subscription && !error,
                    children: renderContent()
                })
            })
        ]
    });
});
export { ExpiredPaymentDeepLinkPage };
